import type {
  OrderChannel,
  OrderLineSource,
  OrderQueueItem,
  OrderStatus,
  PaymentMethod,
} from '@cuito/types';
import { apiClient } from '../../lib/apiClient';
import type { Paginated } from '../../api/catalog';

/**
 * 收银端订单端点的**唯一**封装（§3.4、§4.4）。
 *
 * 形状来源：契约 §12.2（队列 / 短码 / 接单 / 挑拣 / 缺货 / 收款 / 交付 / 手动建单 / 备注）
 * 与 §12.1（详情）。**同一形状只有一处声明**：
 *   · `OrderQueueItem` **直接复用** `@cuito/types` 的同名投影（与 `GET /orders` 的 `items[]`
 *     以及顾客端的历史列表同一个类型）—— 本文件只 re-export，不再抄一份；
 *   · 详情/响应类型（`OrderDetail` / `PickResponse` / `ShortResponse` / `CreatedOrder`）目前
 *     只有收银端用，按本仓惯例「app 的 `api/*.ts` 拥有自己的视图类型」声明在这里
 *     （`api/shelves.ts` 的 `GridCellView`、`api/stock.ts` 的 `LocationView` 同款）。
 *     字段名与契约逐字一致；`@cuito/types` 落地同名类型时改为 re-export 即可。
 *
 * 三个**追加**函数（§4.4 的冻结清单里没有，但页面必须有出口）：
 *   · `fetchOrderDetail`（`GET /orders/:id`）—— 挑拣面板要逐行 `pickedQty/shortQty/done`，
 *     而详情页的路由参数是 `:orderNo`（§4.3）。
 *   · `findOrderIdByNo` —— `orderNo` → `id`，走 `GET /orders` 的 **`orderNo` 精确过滤**
 *     （契约 §12.2 + 附录 A22）。
 *   · `updateOrderNotes` 之外的 `resolveShortCode` 等按 §4.4 逐字实现。
 */

export type { OrderQueueItem } from '@cuito/types';

/** 订单详情的一行（契约 §12.1：`POST /orders` 的行 + Phase 5 新增的 `pickedQty` / `shortQty` / `done`） */
export interface OrderDetailLine {
  id: string;
  productId: string;
  productName: string;
  quantity: number;
  originalPrice: string;
  unitPrice: string;
  discountCents: number;
  totalPrice: string;
  lineSource: OrderLineSource;
  schemeId: string | null;
  schemeTitle: string | null;
  shelf: { shelfId: string; code: string } | null;
  ownerTenantId?: string;
  pickedQty: number;
  shortQty: number;
  /** **服务端下发的唯一判据** `isLineCovered(line)`；客户端不得重算（§4.5） */
  done: boolean;
}

export interface OrderAppliedPromotion {
  schemeId: string;
  schemeTitle: string;
  discountCents: number;
}

/** `GET /orders/:id` 与 `POST /orders/resolve-short-code` 的同构响应（契约 §12.1 / §12.2） */
export interface OrderDetail {
  id: string;
  orderNo: string;
  shortCode: string;
  status: OrderStatus;
  channel: OrderChannel;
  locationId: string;
  totalAmount: string;
  expiresAt: string | null;
  appliedPromotions: OrderAppliedPromotion[];
  lines: OrderDetailLine[];
  locationCode?: string;
  refundedAmount?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  paidConfirmedAt?: string | null;
  acceptedAt?: string | null;
  completedAt?: string | null;
  cancelledAt?: string | null;
  cancelReason?: string | null;
}

export interface PickLineResult {
  lineId: string;
  quantity: number;
  pickedQty: number;
  /** 只有 `POST /orders/:id/short` 的响应行带它（§12.2）；`pick` 的响应里没有这个字段 */
  shortQty?: number;
  done: boolean;
}

/** `POST /orders/:id/pick` 的 200（契约 §12.2） */
export interface PickResponse {
  id: string;
  status: OrderStatus;
  lines: PickLineResult[];
  allPicked: boolean;
  paidConfirmed: boolean;
  statusChangedTo: OrderStatus | null;
}

/** `POST /orders/:id/short` 的 200：在 `pick` 的基础上多三个金额/库存字段（契约 §12.2） */
export interface ShortResponse extends PickResponse {
  totalAmount: string;
  totalAmountBefore: string;
  releasedQuantity: number;
}

export interface CreatedOrder extends OrderDetail {
  orderCode: {
    protocol: string;
    totalLines: number;
    totalPages: number;
    pages: Array<{ page: number; totalPages: number; lineCount: number; payload: string }>;
  };
}

export function fetchOrderQueue(params: {
  locationId: string;
  status?: OrderStatus;
  /**
   * 订单号**精确过滤**（契约 §12.2 的 Phase 5 增量参数、附录 A22）。
   *
   * ⚠️ §4.4 的冻结签名只列了 `locationId` / `status` / `page` / `pageSize`：这一条是**追加**
   * （可选，不影响任何既有调用形状）。它存在的唯一理由与 A22 的裁决一致 —— 收银端的路由是
   * `/cashier/orders/:orderNo`，冷启动时只有订单号，而详情端点收 uuid；
   * 用「拉前 100 行再 find」代替过滤会随数据增长**静默失效**（第 101 张单之后永远找不到）。
   */
  orderNo?: string;
  page?: number;
  pageSize?: number;
}): Promise<OrderQueueItem[]> {
  // 端点是分页包裹（§12.2），而冻结签名要的是数组：展开在这一处，
  // 页面拿到的是「队列」而不是「一页响应」。分页字段本 Phase 用不到（pageSize=100 封顶）。
  return apiClient
    .get<Paginated<OrderQueueItem>>('/orders', { params })
    .then((page) => page.items);
}

export function fetchOrderDetail(orderId: string): Promise<OrderDetail> {
  return apiClient.get<OrderDetail>(`/orders/${orderId}`);
}

/**
 * 订单号 → 订单 id。
 *
 * ⚠️ 这是**绕路**：契约没有 `GET /orders/by-no/:orderNo`，`GET /orders/:id` 收的是 uuid，
 * 而路由参数是 `:orderNo`（§4.3 冻结）⇒ 只能借队列端点的 `orderNo` 过滤（A22）。
 * 找不到（`null`）时页面必须说「订单不存在」，**不许**猜一个 id 去请求。
 */
export function findOrderIdByNo(locationId: string, orderNo: string): Promise<string | null> {
  return fetchOrderQueue({ locationId, orderNo, pageSize: 1 }).then(
    (items) => items.find((item) => item.orderNo === orderNo)?.id ?? null,
  );
}

export function resolveShortCode(input: { shortCode: string; locationId: string }): Promise<OrderDetail> {
  return apiClient.post<OrderDetail>('/orders/resolve-short-code', input);
}

export function acceptOrder(
  orderId: string,
): Promise<{ id: string; status: OrderStatus; acceptedAt: string; acceptedBy: { id: string; name: string | null } }> {
  return apiClient.post(`/orders/${orderId}/accept`);
}

/** 挑拣是**累计值**语义（§12.2）：发的是「这一行现在一共挑了多少」，不是增量 */
export function pickLines(
  orderId: string,
  lines: Array<{ lineId: string; pickedQty: number }>,
): Promise<PickResponse> {
  return apiClient.post<PickResponse>(`/orders/${orderId}/pick`, { lines });
}

/** 缺货同样是累计值（§12.2 `POST /orders/:id/short`），且 `shortQty` 单调不减 */
export function shortLines(
  orderId: string,
  lines: Array<{ lineId: string; shortQty: number }>,
): Promise<ShortResponse> {
  return apiClient.post<ShortResponse>(`/orders/${orderId}/short`, { lines });
}

/**
 * 确认收款的请求体（契约 §12.2）。
 *
 * Phase 9 追加两个**可选**字段（同一行的增量，不是新端点）：
 *   · `origin = 'OFFLINE_SCAN'` —— **只声明这一笔的来路**，不参与任何金额或库存计算；
 *   · `verifiedWithCustomer: true` —— 「已与顾客核对订单」（§6.4 规则 5b 的勾选）。
 *
 * ⚠️ **两个字段成对**（静态④ verifygate 判的就是这一对）：`origin = 'OFFLINE_SCAN'` 而
 * `verifiedWithCustomer !== true` ⇒ 服务端 422 `CUSTOMER_VERIFICATION_REQUIRED`。
 * 因此 `verifiedWithCustomer` 的类型是字面量 `true` 而不是 `boolean`：**类型上就写不出**
 * 「带了来路却没带核对」那个中间态。`origin` 缺省（联网路径）时服务端忽略
 * `verifiedWithCustomer`，那时请求体里一个多余字段都不带（13 套 e2e 的回归断言）。
 */
export interface ConfirmPaymentInput {
  method: PaymentMethod;
  note?: string;
  origin?: 'OFFLINE_SCAN';
  verifiedWithCustomer?: true;
}

export function confirmPayment(
  orderId: string,
  input: ConfirmPaymentInput,
): Promise<{ id: string; paidConfirmed: boolean; paidConfirmedAt: string; statusChangedTo: OrderStatus }> {
  return apiClient.post(`/orders/${orderId}/confirm-payment`, input);
}

export function completeOrder(orderId: string): Promise<{
  id: string;
  status: 'COMPLETED';
  completedAt: string;
  soldQty: Array<{ productId: string; quantity: number }>;
}> {
  return apiClient.post(`/orders/${orderId}/complete`);
}

/** 店员手动建单（§12.2）：与顾客单**同一条定价路径**，响应含 `appliedPromotions` */
export function createStaffOrder(input: {
  locationId: string;
  items: Array<{ productId: string; quantity: number; shelfId?: string }>;
}): Promise<CreatedOrder> {
  return apiClient.post<CreatedOrder>('/orders/staff', input);
}

/**
 * 订单备注（契约 §12.2 第 11 行，`REQ-LEDGER-001`）。
 *
 * **方法必须是 `POST`**：契约那一行登记的是 `POST /orders/:id/notes`（整字段覆盖、
 * 空串表示清空）。这里曾经写成 `apiClient.patch`（计划附录 B1l）—— 页面单测把整个
 * `api/orders` 模块 mock 掉，所以谁也不会红；现在由 `api/orders.spec.ts` 在**传输边界**
 * 上钉住方法与路径。
 */
export function updateOrderNotes(
  orderId: string,
  notes: string,
): Promise<{ id: string; notes: string; updatedAt: string }> {
  return apiClient.post(`/orders/${orderId}/notes`, { notes });
}
